import React, { useMemo, useState } from "react";
import { useNinaMemory } from "../context/NinaMemoryContext";
import {
  NINA_MEMORY_CATEGORIES,
  NINA_EVIDENCE_STATUSES,
  NINA_RISK_LEVELS,
} from "../nina/memory/NinaMemorySchema";
import "./NinaLearningLog.css";

const EMPTY_FORM = {
  category: "lesson-learned",
  title: "",
  details: "",
  symbol: "",
  source: "manual-learning-log",
  sourceRecordId: "",
  evidenceStatus: "user-reported",
  confidence: "",
  outcome: "",
  lesson: "",
  riskLevel: "none",
  userApproved: false,
};

const LABELS = {
  "academy-progress": "Academy progress",
  "simulated-trade": "Legacy non-live trades",
  "trade-plan-decision": "Trade-plan decisions",
  mistake: "Mistakes",
  "lesson-learned": "Lessons learned",
  "nina-observation": "AI observations",
  "user-approved-rule": "User-approved rules",
};

const CATEGORY_GUIDANCE = {
  "academy-progress": "A read-only signal showing completed Academy work.",
  "simulated-trade": "A legacy non-live record kept only for backward compatibility.",
  "trade-plan-decision": "A choice made while preparing or following a trade plan.",
  mistake: "An action or decision that should be avoided or corrected.",
  "lesson-learned": "A reusable insight gained from an experience or outcome.",
  "nina-observation": "Something the AI Assistant noticed for review; it is not an approved rule.",
  "user-approved-rule": "A personal trading rule you explicitly approve and choose to follow.",
};

const EVIDENCE_LABELS = {
  verified: "verified",
  simulated: "legacy non-live",
  "user-reported": "user-reported",
  "system-derived": "system-derived",
  hypothesis: "hypothesis",
};

const EVIDENCE_GUIDANCE = {
  verified: "Confirmed with reliable evidence or a completed result.",
  simulated: "Legacy non-live evidence retained only for backward compatibility.",
  "user-reported": "Entered by the user from personal observation or experience.",
  "system-derived": "Created from information already recorded by AninaTrade.",
  hypothesis: "An idea that still needs testing or confirmation.",
};

const RISK_GUIDANCE = {
  none: "No meaningful trading risk is associated with ignoring this lesson.",
  low: "Ignoring it may cause a small or limited problem.",
  medium: "Ignoring it could cause a meaningful mistake or loss.",
  high: "Ignoring it could cause a serious trading mistake or loss.",
  critical: "Ignoring it could create severe financial or safety consequences.",
};

function downloadJson(contents) {
  const blob = new Blob([contents], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `aninatrade-ai-learning-log-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 100);
}

const safeErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  if (typeof error?.message === "string") return error.message;
  return fallback;
};

const CONFIDENCE_ERROR = "Enter a whole confidence level from 0 to 100.";

const CONFIDENCE_EDITING_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "Enter",
  "Escape",
  "Home",
  "End",
  "ArrowLeft",
  "ArrowRight",
]);

const getConfidenceError = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) &&
    Number.isInteger(numericValue) &&
    numericValue >= 0 &&
    numericValue <= 100
    ? ""
    : CONFIDENCE_ERROR;
};

export default function NinaLearningLog() {
  const {
    records,
    summary,
    error,
    createRecord,
    updateRecord,
    archiveRecord,
    restoreRecord,
    clearRecords,
    exportRecords,
  } = useNinaMemory();
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    evidence: "all",
    risk: "all",
    status: "active",
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [message, setMessage] = useState("");
  const [confidenceError, setConfidenceError] = useState("");

  const visibleRecords = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    return records.filter((record) => {
      const searchable = [
        record.title,
        record.details,
        record.symbol,
        record.outcome,
        record.lesson,
        record.source,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return (
        (!query || searchable.includes(query)) &&
        (filters.category === "all" || record.category === filters.category) &&
        (filters.evidence === "all" ||
          record.evidenceStatus === filters.evidence) &&
        (filters.risk === "all" || record.riskLevel === filters.risk) &&
        (filters.status === "all" || record.status === filters.status)
      );
    });
  }, [filters, records]);

  const selectedRecord = records.find((record) => record.id === selectedId);

  const setField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name === "confidence") {
      setConfidenceError(getConfidenceError(value));
    }
  };

  const handleConfidenceKeyDown = (event) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const current = Number(form.confidence);
      const safeCurrent =
        form.confidence !== "" && Number.isInteger(current) ? current : 0;
      const change = event.key === "ArrowUp" ? 1 : -1;
      const next = Math.min(100, Math.max(0, safeCurrent + change));
      setForm((value) => ({ ...value, confidence: String(next) }));
      setConfidenceError("");
      return;
    }

    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      CONFIDENCE_EDITING_KEYS.has(event.key) ||
      /^\d$/.test(event.key) ||
      event.key.length > 1
    ) {
      return;
    }

    event.preventDefault();
    setConfidenceError(CONFIDENCE_ERROR);
  };

  const handleConfidencePaste = (event) => {
    const pastedValue = event.clipboardData?.getData("text") ?? "";
    if (/^\d+$/.test(pastedValue.trim())) return;
    event.preventDefault();
    setConfidenceError(CONFIDENCE_ERROR);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setConfidenceError("");
  };

  const submit = (event) => {
    event.preventDefault();
    const confidenceValidationError =
      confidenceError || getConfidenceError(form.confidence);
    if (confidenceValidationError) {
      setConfidenceError(confidenceValidationError);
      setMessage("");
      return;
    }
    const input = {
      ...form,
      confidence: form.confidence === "" ? null : Number(form.confidence),
      timestamp: editingId
        ? records.find((record) => record.id === editingId)?.timestamp
        : new Date().toISOString(),
    };
    const result = editingId
      ? updateRecord(editingId, input)
      : createRecord(input);
    if (result.ok) {
      setMessage(editingId ? "Memory updated." : "Memory added.");
      resetForm();
    } else {
      setMessage(
        safeErrorMessage(result.error, "The memory could not be saved.")
      );
    }
  };

  const startEdit = (record) => {
    if (record.readOnly) return;
    setEditingId(record.id);
    setForm({
      category: record.category,
      title: record.title || "",
      details: record.details || "",
      symbol: record.symbol || "",
      source: record.source || "manual-learning-log",
      sourceRecordId: record.sourceRecordId || "",
      evidenceStatus: record.evidenceStatus,
      confidence: record.confidence ?? "",
      outcome: record.outcome || "",
      lesson: record.lesson || "",
      riskLevel: record.riskLevel,
      userApproved: record.userApproved === true,
    });
  };

  const confirmArchive = (record) => {
    if (
      window.confirm(
        `Archive “${record.title || LABELS[record.category]}”? You can restore it later.`
      )
    ) {
      const result = archiveRecord(record.id);
      setMessage(
        result.ok ? "Memory archived." : result.error?.message || "Archive failed."
      );
    }
  };

  const confirmClear = () => {
    if (
      window.confirm(
        "Clear every AI memory record stored by the Learning Log in this browser? Academy progress is not affected. This cannot be undone."
      )
    ) {
      const result = clearRecords({ confirmed: true });
      setMessage(
        result.ok
          ? "Local AI memories cleared."
          : result.error?.message || "Clear failed."
      );
      setSelectedId(null);
    }
  };

  const exportBackup = () => {
    const result = exportRecords();
    if (result.ok) {
      downloadJson(result.value);
      setMessage("JSON backup exported.");
    } else {
      setMessage(result.error?.message || "The backup could not be exported.");
    }
  };

  return (
    <main className="nina-log" aria-labelledby="nina-log-title">
      <header className="nina-log__header">
        <div>
          <p className="nina-log__eyebrow">AninaTrade AI Assistant</p>
          <h1 id="nina-log-title">Secure Memory &amp; Learning Log</h1>
          <p className="nina-log__safety">
            Stored only in this browser. No brokerage, order placement, network
            sync, or credential storage is used.
          </p>
        </div>
        <div className="nina-log__header-actions">
          <button type="button" onClick={exportBackup}>
            Export JSON
          </button>
          <button type="button" className="danger" onClick={confirmClear}>
            Clear local memory
          </button>
        </div>
      </header>

      {(message || error) && (
        <p className="nina-log__message" role="status">
          {safeErrorMessage(message || error, "AI memory is unavailable.")}
        </p>
      )}

      <section className="nina-log__summary" aria-label="Memory summary">
        <article><strong>{summary.total}</strong><span>Total records</span></article>
        <article><strong>{summary.active}</strong><span>Active</span></article>
        <article><strong>{summary.archived}</strong><span>Archived</span></article>
        <article><strong>{summary.academyProgress}</strong><span>Academy signals</span></article>
      </section>

      <section className="nina-log__panel nina-log__guidance" aria-labelledby="nina-memory-guide-title">
        <div className="nina-log__guidance-intro">
          <p className="nina-log__eyebrow">START HERE</p>
          <h2 id="nina-memory-guide-title">What is AI Memory?</h2>
          <p>
            AI Memory saves useful trading experiences, decisions, outcomes,
            and lessons so the AI Assistant can recognize repeated behavior and provide
            better coaching over time.
          </p>
          <ul className="nina-log__safety-list">
            <li>Records stay only in this browser.</li>
            <li>AI Memory does not connect to a brokerage and cannot place trades.</li>
            <li>AI Memory does not guarantee trading results.</li>
            <li>Never enter passwords, account numbers, API keys, or other credentials.</li>
          </ul>
        </div>

        <section className="nina-log__checklist" aria-labelledby="nina-memory-checklist-title">
          <h3 id="nina-memory-checklist-title">First-time-use checklist</h3>
          <ol>
            <li>Choose the type of memory.</li>
            <li>Describe what you observed.</li>
            <li>Record what happened.</li>
            <li>Write the lesson for the next similar situation.</li>
            <li>Choose the evidence, risk, and confidence levels.</li>
            <li>Review the information and click “Add memory.”</li>
          </ol>
        </section>

        <div className="nina-log__guidance-grid">
          <article>
            <h3>How the AI Assistant uses a memory</h3>
            <p>A record helps the AI Assistant compare:</p>
            <ul>
              <li>what the trader observed;</li>
              <li>what decision was made;</li>
              <li>what happened afterward; and</li>
              <li>what lesson should guide the next similar situation.</li>
            </ul>
            <p className="nina-log__guidance-note">
              Version 1 stores and organizes information. It does not
              automatically control trading decisions.
            </p>
          </article>

          <article>
            <h3>How to add a memory</h3>
            <dl className="nina-log__field-guide">
              <dt>Category</dt><dd>What kind of memory this is.</dd>
              <dt>Title</dt><dd>A short name for the event or lesson.</dd>
              <dt>Symbol</dt><dd>The related ticker, if applicable.</dd>
              <dt>Evidence</dt><dd>How the information is known: verified, user-reported, system-derived, hypothesis, or a legacy non-live record.</dd>
              <dt>Risk</dt><dd>The seriousness of the situation if the lesson is ignored.</dd>
              <dt>Confidence</dt><dd>Certainty about this memory from 0–100. It is not a prediction that a stock will rise or fall.</dd>
              <dt>Details</dt><dd>What happened or what was observed.</dd>
              <dt>Outcome</dt><dd>What result followed.</dd>
              <dt>Lesson</dt><dd>What should be repeated, avoided, or changed next time.</dd>
            </dl>
          </article>
        </div>

        <article className="nina-log__example" aria-label="Example memory">
          <h3>Example</h3>
          <p><strong>Details:</strong> “Price moved above VWAP, but volume weakened and MACD did not confirm.”</p>
          <p><strong>Outcome:</strong> “The breakout failed and the entry would have produced a loss.”</p>
          <p><strong>Lesson:</strong> “Wait for volume and momentum confirmation before entering.”</p>
        </article>

        <div className="nina-log__learn-more">
          <details>
            <summary>Learn more: What do the selectable values mean?</summary>
            <div className="nina-log__definitions">
              <section>
                <h4>Categories</h4>
                <dl>
                  {NINA_MEMORY_CATEGORIES.map((category) => (
                    <React.Fragment key={category}>
                      <dt>{LABELS[category]}</dt>
                      <dd>{CATEGORY_GUIDANCE[category]}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
              <section>
                <h4>Evidence</h4>
                <dl>
                  {NINA_EVIDENCE_STATUSES.map((status) => (
                    <React.Fragment key={status}>
                      <dt>{EVIDENCE_LABELS[status]}</dt>
                      <dd>{EVIDENCE_GUIDANCE[status]}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
              <section>
                <h4>Risk</h4>
                <dl>
                  {NINA_RISK_LEVELS.map((risk) => (
                    <React.Fragment key={risk}>
                      <dt>{risk}</dt>
                      <dd>{RISK_GUIDANCE[risk]}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
            </div>
          </details>

          <details>
            <summary>Learn more: What happens after I click “Add memory”?</summary>
            <p>
              The AI Assistant validates the information and saves the record only in this
              browser. The new record appears in the Learning Log and updates
              the summary cards. If required information is missing or unsafe,
              the record is not saved and a message explains what needs attention.
            </p>
          </details>

          <details>
            <summary>Learn more: How do I manage my records?</summary>
            <ul>
              <li><strong>Review:</strong> Select “Details” to see the complete record.</li>
              <li><strong>Edit:</strong> Select “Edit,” update the information, and save the changes.</li>
              <li><strong>Archive:</strong> Select “Archive” and approve the confirmation. Archived records remain available.</li>
              <li><strong>Restore:</strong> Select the Archived status filter, open the archived memory, and choose “Restore” to return it to Active records.</li>
              <li><strong>Filter:</strong> Use search, category, evidence, risk, and status controls to narrow the Learning Log.</li>
              <li><strong>Export:</strong> Select “Export JSON” to download a user-owned backup of valid AI Memory records.</li>
              <li>
                <strong>Clear local memory:</strong> Export a JSON backup first
                if you may need the records later. Then select “Clear local
                memory” and review and approve the confirmation. This
                permanently removes manually saved AI Memory records from
                this browser. Read-only Academy progress is stored separately
                and is not deleted.
              </li>
            </ul>
          </details>
        </div>

        <aside className="nina-log__academy-note">
          <h3>Academy progress</h3>
          <p>
            Academy progress appears as read-only, system-derived signals.
            These records show what you completed and cannot be edited or
            archived from AI Memory.
          </p>
        </aside>
      </section>

      <section className="nina-log__panel">
        <h2>{editingId ? "Edit memory" : "Add a memory"}</h2>
        <form className="nina-log__form" onSubmit={submit} noValidate>
          <label>
            Category
            <select name="category" aria-label="Category" value={form.category} onChange={setField} aria-describedby="memory-category-help">
              {NINA_MEMORY_CATEGORIES.filter(
                (category) =>
                  category !== "academy-progress" &&
                  category !== "simulated-trade"
              ).map((category) => (
                <option key={category} value={category}>{LABELS[category]}</option>
              ))}
            </select>
            <span id="memory-category-help" className="nina-log__helper">What kind of memory this is.</span>
          </label>
          <label>
            Title
            <input name="title" aria-label="Title" value={form.title} onChange={setField} aria-describedby="memory-title-help" />
            <span id="memory-title-help" className="nina-log__helper">A short name for the event or lesson.</span>
          </label>
          <label>
            Symbol
            <input name="symbol" aria-label="Symbol" value={form.symbol} onChange={setField} placeholder="Optional" aria-describedby="memory-symbol-help" />
            <span id="memory-symbol-help" className="nina-log__helper">The related ticker, if applicable.</span>
          </label>
          <label>
            Evidence
            <select name="evidenceStatus" aria-label="Evidence" value={form.evidenceStatus} onChange={setField} aria-describedby="memory-evidence-help">
              {NINA_EVIDENCE_STATUSES.filter(
                (status) => status !== "simulated"
              ).map((status) => (
                <option key={status} value={status}>{EVIDENCE_LABELS[status]}</option>
              ))}
            </select>
            <span id="memory-evidence-help" className="nina-log__helper">How this information is known.</span>
          </label>
          <label>
            Risk
            <select name="riskLevel" aria-label="Risk" value={form.riskLevel} onChange={setField} aria-describedby="memory-risk-help">
              {NINA_RISK_LEVELS.map((risk) => (
                <option key={risk} value={risk}>{risk}</option>
              ))}
            </select>
            <span id="memory-risk-help" className="nina-log__helper">The seriousness of the situation if this lesson is ignored.</span>
          </label>
          <label>
            Confidence level (0–100)
            <input
              name="confidence"
              aria-label="Confidence level (0–100)"
              type="number"
              min="0"
              max="100"
              step="1"
              value={form.confidence}
              onChange={setField}
              onKeyDown={handleConfidenceKeyDown}
              onPaste={handleConfidencePaste}
              placeholder="Optional"
              aria-invalid={confidenceError ? "true" : "false"}
              aria-describedby={
                confidenceError
                  ? "memory-confidence-help memory-confidence-error"
                  : "memory-confidence-help"
              }
            />
            <span id="memory-confidence-help" className="nina-log__helper">
              How certain you are that this memory is accurate—not the probability of a stock-price movement.
            </span>
            {confidenceError && (
              <span
                id="memory-confidence-error"
                className="nina-log__field-error"
                role="alert"
              >
                {confidenceError}
              </span>
            )}
          </label>
          <label className="nina-log__wide">
            Details
            <textarea name="details" aria-label="Details" value={form.details} onChange={setField} aria-describedby="memory-details-help" />
            <span id="memory-details-help" className="nina-log__helper">What happened or what was observed.</span>
          </label>
          <label>
            Outcome
            <input name="outcome" aria-label="Outcome" value={form.outcome} onChange={setField} aria-describedby="memory-outcome-help" />
            <span id="memory-outcome-help" className="nina-log__helper">What result followed.</span>
          </label>
          <label>
            Lesson
            <input name="lesson" aria-label="Lesson" value={form.lesson} onChange={setField} aria-describedby="memory-lesson-help" />
            <span id="memory-lesson-help" className="nina-log__helper">What should be repeated, avoided, or changed next time.</span>
          </label>
          {form.category === "user-approved-rule" && (
            <label className="nina-log__approval nina-log__wide">
              <input
                name="userApproved"
                type="checkbox"
                checked={form.userApproved}
                onChange={setField}
              />
              I explicitly approve saving this as my rule.
            </label>
          )}
          <div className="nina-log__form-actions nina-log__wide">
            <button type="submit">{editingId ? "Save changes" : "Add memory"}</button>
            {editingId && <button type="button" onClick={resetForm}>Cancel</button>}
          </div>
        </form>
      </section>

      <section className="nina-log__panel">
        <h2>Learning Log</h2>
        <div className="nina-log__filters" aria-label="Learning Log filters">
          <label>
            Search
            <input
              value={filters.search}
              onChange={(event) =>
                setFilters({ ...filters, search: event.target.value })
              }
            />
          </label>
          <label>
            Category
            <select
              value={filters.category}
              onChange={(event) =>
                setFilters({ ...filters, category: event.target.value })
              }
            >
              <option value="all">All categories</option>
              {NINA_MEMORY_CATEGORIES.map((category) => (
                <option key={category} value={category}>{LABELS[category]}</option>
              ))}
            </select>
          </label>
          <label>
            Evidence
            <select
              value={filters.evidence}
              onChange={(event) =>
                setFilters({ ...filters, evidence: event.target.value })
              }
            >
              <option value="all">All evidence</option>
              {NINA_EVIDENCE_STATUSES.map((status) => (
                <option key={status} value={status}>{EVIDENCE_LABELS[status]}</option>
              ))}
            </select>
          </label>
          <label>
            Risk
            <select
              value={filters.risk}
              onChange={(event) =>
                setFilters({ ...filters, risk: event.target.value })
              }
            >
              <option value="all">All risk levels</option>
              {NINA_RISK_LEVELS.map((risk) => (
                <option key={risk} value={risk}>{risk}</option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={filters.status}
              onChange={(event) =>
                setFilters({ ...filters, status: event.target.value })
              }
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="all">Active and archived</option>
            </select>
          </label>
        </div>

        <div className="nina-log__records">
          {visibleRecords.length === 0 && <p>No matching memories.</p>}
          {visibleRecords.map((record) => (
            <article
              key={record.id}
              className={`nina-log__record evidence-${record.evidenceStatus}`}
            >
              <div>
                <p className="nina-log__record-meta">
                  <span>{LABELS[record.category]}</span>
                  <span>{EVIDENCE_LABELS[record.evidenceStatus] || record.evidenceStatus}</span>
                  <span>{record.riskLevel} risk</span>
                  {record.readOnly && <span>read-only</span>}
                </p>
                <h3>{record.title || LABELS[record.category]}</h3>
                <p>{record.details || record.lesson || record.outcome}</p>
              </div>
              <div className="nina-log__record-actions">
                <button type="button" onClick={() => setSelectedId(record.id)}>
                  Details
                </button>
                {!record.readOnly && record.status === "active" && (
                  <>
                    <button type="button" onClick={() => startEdit(record)}>Edit</button>
                    <button type="button" onClick={() => confirmArchive(record)}>Archive</button>
                  </>
                )}
                {!record.readOnly && record.status === "archived" && (
                  <button type="button" onClick={() => restoreRecord(record.id)}>
                    Restore
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {selectedRecord && (
        <section className="nina-log__panel nina-log__details" aria-label="Memory details">
          <div>
            <h2>{selectedRecord.title || LABELS[selectedRecord.category]}</h2>
            <p>{selectedRecord.details || "No additional details."}</p>
            <dl>
              <dt>Created</dt><dd>{selectedRecord.createdAt}</dd>
              <dt>Source</dt><dd>{selectedRecord.source}</dd>
              <dt>Symbol</dt><dd>{selectedRecord.symbol || "—"}</dd>
              <dt>Outcome</dt><dd>{selectedRecord.outcome || "—"}</dd>
              <dt>Lesson</dt><dd>{selectedRecord.lesson || "—"}</dd>
            </dl>
          </div>
          <button type="button" onClick={() => setSelectedId(null)}>Close</button>
        </section>
      )}
    </main>
  );
}
